import React from 'react';
import type { FileError } from 'react-dropzone';
import { VyDialog } from '../../../../feedback/VyDialog';
import { withSuspenseBoundary } from '../../../../../utility/SuspenseBoundary';
import { ImportContractDropzone } from './ImportContractDropzone';
import { ImportContractLoadingState } from './ImportContractLoadingState';
import { ImportContractErrorState } from './ImportContractErrorState';
import { ImportContractFilePreview } from './ImportContractFilePreview';
import type { DialogPhase } from './use-import-contract-dialog';
import { useImportContractDialog } from './use-import-contract-dialog';
import { isNotEmptyArray } from '@vy/utils';

type ImportContractDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const ImportContractDialog: React.FC<ImportContractDialogProps> = ({ open, onClose }) => {
  const {
    files,
    errorMessage,
    phase,
    handleDeleteFile,
    handleClose,
    handleTryAgain,
    handleSubmit,
    handleFilesSelect,
    handleFileError,
  } = useImportContractDialog(onClose);

  const hasFiles = isNotEmptyArray(files);
  const isSubmitting = phase === 'submitting';
  const isError = phase === 'error';

  const ctaText = isError ? 'Try Again' : isSubmitting ? 'Uploading...' : 'Upload';
  const ctaDisabled = isError ? false : !hasFiles || isSubmitting;
  const onSubmit = isError ? handleTryAgain : handleSubmit;

  return (
    <VyDialog
      open={open}
      onClose={handleClose}
      label="Extract data from contract"
      labelDescription="Upload one or more PDF contracts to begin"
      cancelButtonText="Cancel"
      ctaText={ctaText}
      ctaDisabled={ctaDisabled}
      onSubmit={onSubmit}
    >
      <ImportContractDialogContent
        phase={phase}
        files={files}
        errorMessage={errorMessage}
        onDeleteFile={handleDeleteFile}
        onFilesSelect={handleFilesSelect}
        onFileError={handleFileError}
      />
    </VyDialog>
  );
};

type ImportContractDialogContentProps = {
  phase: DialogPhase;
  files: File[];
  errorMessage: FileError | null;
  onDeleteFile: (file: File) => void;
  onFilesSelect: (files: File[]) => void;
  onFileError: (error: FileError) => void;
};

const ImportContractDialogContentBase: React.FC<ImportContractDialogContentProps> = ({
  phase,
  files,
  errorMessage,
  onDeleteFile,
  onFilesSelect,
  onFileError,
}) => {
  const hasFiles = isNotEmptyArray(files);

  if (phase === 'error') return <ImportContractErrorState />;

  if (hasFiles) {
    return (
      <ImportContractFilePreview
        files={files}
        onDelete={onDeleteFile}
      />
    );
  }

  return (
    <ImportContractDropzone
      onFilesSelect={onFilesSelect}
      onFileError={onFileError}
      errorMessage={errorMessage}
    />
  );
};

const ImportContractDialogContent = withSuspenseBoundary(ImportContractDialogContentBase, {
  suspenseProps: { fallback: <ImportContractLoadingState /> },
});
